/**
 * "Open X" voice commands: opens a matching app, else a matching document.
 * Only one mounted widget may listen, or a command would run twice.
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useVoiceAgent } from '@/contexts/VoiceAgentContext'
import { APPS } from '@/data/apps'
import { logError } from '@/lib/logger'
import { openFile } from '@/lib/files/openFile'
import { cdbTqsApiService } from '@/services/cdb-tqs-api-service'
import type { WidgetProps } from '@/types/widget'

export function useVoiceCommands(onOpenApp: WidgetProps['onOpenApp'], enabled = true) {
  const { t } = useTranslation()
  const { state } = useVoiceAgent()
  const lastProcessedMessageIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled) return

    const processCommands = async () => {
      if (!state.transcript.length) return

      const lastMessage = state.transcript[state.transcript.length - 1]

      // Only process user messages that haven't been processed yet
      if (lastMessage.role !== 'user' || lastMessage.id === lastProcessedMessageIdRef.current) {
        return
      }

      lastProcessedMessageIdRef.current = lastMessage.id
      const text = lastMessage.content.toLowerCase().trim()
      const openMatch = text.match(/^(?:open|öffne)\s+(.+)$/i)
      if (!openMatch) return

      const targetName = openMatch[1].trim()

      const matchedApp = APPS.find((app) => {
        const appTitle = t(`apps.${app.id}`).toLowerCase()
        return appTitle === targetName || appTitle.includes(targetName)
      })

      if (matchedApp) {
        onOpenApp(matchedApp.id)
        return
      }

      try {
        // There is no search endpoint for this yet, so check the recent documents.
        const docsResponse = await cdbTqsApiService.getDocuments(undefined, 20)
        const matchedDoc = docsResponse.documents.find(
          (doc) =>
            (doc.title && doc.title.toLowerCase().includes(targetName)) ||
            doc.filename.toLowerCase().includes(targetName),
        )

        if (matchedDoc) {
          openFile(
            {
              kind: 'knowledge-area',
              documentId: matchedDoc.document_id,
              knowledgeArea: matchedDoc.knowledge_area,
              fileName: matchedDoc.filename,
            },
            (app, _title, _subtitle, params) => onOpenApp(app, params),
          )
        }
      } catch (error) {
        logError(error as Error, { context: 'useVoiceCommands.findDocument' })
      }
    }

    void processCommands()
  }, [enabled, state.transcript, onOpenApp, t])
}
